import { useRef, useState, useCallback, useEffect } from 'react'

import { toast } from '@buffer-mono/popcorn'

import type { Channel } from '~publish/gql/graphql'

import type { FilterStatus } from '../../CommentsFilters/types'
import { triggerEngagementStart } from '../../CommentsList/hooks/useEngagementTimer'
import { triggerEngagementScoreStart } from '../../CommentsList/utils/commentScoreSessionManager'
import { useCommentData } from '../../context/CommentContext'
import { useReplyToComment } from '../../hooks/useReplyToComment'

// Constants
const OPTIMISTIC_REPLY_DURATION = 800

type UseReplySubmissionProps = {
  channel: Channel
  currentStatusFilter?: FilterStatus
  onAnimateOut: (shouldAnimate: boolean) => void
  onFormClear: () => void
  onFormRestore: (text: string) => void
  onFormCollapse: () => void
  getCurrentText: () => string
  onReplySubmitted?: () => void
}

type UseReplySubmissionReturn = {
  isSubmitting: boolean
  isOptimistic: boolean
  error?: string
  errorType?: string
  pendingReply?: string
  submit: (text?: string) => Promise<void>
  retry: () => Promise<void>
}

/**
 * Hook for managing comment reply submission with optimistic updates.
 *
 * This hook handles the complete submission flow including:
 * - API calls to submit replies
 * - Optimistic UI updates for immediate feedback
 * - Error handling and recovery
 * - Engagement tracking and analytics
 * - Animation control based on filter status
 *
 * **When to use:**
 * - When you need to submit comment replies with optimistic updates
 * - For components that require immediate user feedback
 * - When you need comprehensive error handling and recovery
 * - For integration with engagement tracking systems
 *
 * **Key Features:**
 * - Optimistic updates with 800ms fallback timer
 * - Filter-specific optimistic behavior (unreplied vs all)
 * - Automatic engagement tracking on success
 * - Smart error handling with CommentDeletedError for deleted comments
 * - Toast notifications for user feedback
 * - Animation control for comment visibility with error reversal
 * - Retry functionality for failed submissions
 *
 * **Optimistic Flow:**
 * 1. User submits → Immediate UI feedback + 800ms timer
 * 2. For unreplied filter → Immediately animate out comment + show success toast
 * 3. For all filter → Collapse form, keep text for optimistic display
 * 4. API call in background → Success/failure handling
 * 5. Timer completion → Success toast for all filter (if API hasn't responded)
 * 6. API response → Confirms success or reverts with error
 *
 * **Error Handling:**
 * - For "not found" errors (deleted comments): Brings comment back, shows CommentDeletedError
 * - For other errors: Restores form state for retry
 * - For unreplied filter: Reverses optimistic animation on any error
 *
 * @param props - Configuration for submission behavior
 * @returns Submission state and handlers
 *
 * @example
 * ```tsx
 * const submission = useReplySubmission({
 *   channel,
 *   currentStatusFilter,
 *   onAnimateOut: setShouldAnimateOut,
 *   onFormClear: form.clear,
 *   onFormRestore: form.setText,
 *   onFormCollapse: form.collapse,
 *   getCurrentText: () => form.text
 * })
 *
 * // Basic usage
 * <button onClick={() => submission.submit()} disabled={submission.isSubmitting}>
 *   {submission.isOptimistic ? 'Sending...' : 'Reply'}
 * </button>
 *
 * {submission.error && <div>Error: {submission.error}</div>}
 * ```
 *
 * @example
 * ```tsx
 * // Error handling
 * switch (submission.errorType) {
 *   case 'not_found': return <CommentDeletedError />
 *   case 'api_error': return <div>Network error</div>
 * }
 *
 * // Custom submission
 * await submission.submit("Quick reply text")
 * ```
 *
 * @example
 * ```tsx
 * // Filter-specific behavior
 * // Unreplied filter: Comment animates out immediately, shows toast
 * // All filter: Shows optimistic state, then toast after 800ms
 *
 * // Error reversal for unreplied filter
 * // Comment comes back with appropriate error state
 * ```
 */
export const useReplySubmission = ({
  channel,
  currentStatusFilter,
  onAnimateOut,
  onFormClear,
  onFormRestore,
  onFormCollapse,
  getCurrentText,
  onReplySubmitted,
}: UseReplySubmissionProps): UseReplySubmissionReturn => {
  const comment = useCommentData()

  // Refs
  const optimisticTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isOptimisticRef = useRef(false)

  // Hooks
  const { replyToComment, loading: isReplying } = useReplyToComment()

  // Optimistic state
  const [isOptimisticReply, setIsOptimisticReply] = useState(false)
  const [pendingReply, setPendingReply] = useState<string | null>(null)
  const [replyError, setReplyError] = useState<string | null>(null)
  const [errorType, setErrorType] = useState<string | null>(null)

  // Computed states
  const isSubmitting = isReplying || isOptimisticReply

  // Helper function to clear optimistic timer
  const clearOptimisticTimer = useCallback((): void => {
    if (optimisticTimerRef.current) {
      clearTimeout(optimisticTimerRef.current)
      optimisticTimerRef.current = null
    }
  }, [])

  // Helper function to handle submission errors
  const handleSubmissionError = useCallback(
    (error: Error | string, errorType: string, finalText: string): void => {
      // Reset optimistic state
      setIsOptimisticReply(false)
      isOptimisticRef.current = false
      setPendingReply(null)

      // Set error state
      const errorMessage = typeof error === 'string' ? error : error.message
      setReplyError(errorMessage)
      setErrorType(errorType)

      // For unreplied filter, bring the comment back
      if (currentStatusFilter === 'unreplied') {
        onAnimateOut(false)
      }

      // Restore form state for non-deleted errors
      if (errorType !== 'not_found') {
        onFormRestore(finalText)
      }
    },
    [currentStatusFilter, onAnimateOut, onFormRestore],
  )

  // Encapsulates logic for a confirmed success, including showing toast
  const handleConfirmedSuccess = useCallback((): void => {
    triggerEngagementStart()
    triggerEngagementScoreStart(channel.id)
  }, [channel.id])

  // Submit logic
  const submit = useCallback(
    async (text?: string): Promise<void> => {
      const finalText = (text || getCurrentText()).trim()
      if (!finalText) return

      try {
        // Set optimistic state
        setIsOptimisticReply(true)
        isOptimisticRef.current = true
        setPendingReply(finalText)
        setReplyError(null)
        setErrorType(null)

        // Notify parent component that reply has been submitted
        onReplySubmitted?.()

        // For unreplied filter, immediately animate out the comment and show success toast
        if (currentStatusFilter === 'unreplied') {
          // Clear form and animate out immediately for unreplied filter
          onFormClear()
          onFormCollapse()
          onAnimateOut(true)
          toast.success('Reply in progress', {
            duration: 1_000,
          })
        } else {
          // For other filters, just collapse the form but keep the text for optimistic display
          onFormCollapse()
        }

        // Start optimistic timer
        optimisticTimerRef.current = setTimeout(() => {
          // Guard against race condition - check if we're still in optimistic state
          if (!isOptimisticRef.current) return

          // Add success toast (only for non-unreplied filters since unreplied shows it immediately)
          if (currentStatusFilter !== 'unreplied') {
            toast.success('Reply in progress', {
              duration: 1_000,
            })
          }
          handleConfirmedSuccess()
          setIsOptimisticReply(false)
          isOptimisticRef.current = false
          setPendingReply(null)
        }, OPTIMISTIC_REPLY_DURATION)

        // Submit to API
        const result = await replyToComment(comment, finalText)

        // Clear timer since API responded
        clearOptimisticTimer()

        if (result.success) {
          // API success - confirm the optimistic update
          handleConfirmedSuccess()
          setIsOptimisticReply(false)
          isOptimisticRef.current = false
          setPendingReply(null)
        } else {
          // API error - revert optimistic state
          // Map GraphQL error types to our internal error types
          const mappedErrorType =
            result.errorType === 'NotFoundError'
              ? 'not_found'
              : result.errorType || 'api_error'
          handleSubmissionError(
            result.error || 'Failed to send reply',
            mappedErrorType,
            finalText,
          )
        }
      } catch (error) {
        // Clear timer since API responded
        clearOptimisticTimer()

        // eslint-disable-next-line no-console
        console.error('An unexpected error occurred during reply:', error)

        // Handle different error types
        if (error instanceof Error) {
          if (
            error.message.includes('not found') ||
            error.message.includes('deleted')
          ) {
            handleSubmissionError(
              'This comment has been deleted',
              'not_found',
              finalText,
            )
          } else {
            handleSubmissionError(error, 'api_error', finalText)
          }
        } else {
          handleSubmissionError(
            'An unexpected error occurred',
            'unknown',
            finalText,
          )
        }
      }
    },
    [
      comment,
      replyToComment,
      clearOptimisticTimer,
      handleConfirmedSuccess,
      handleSubmissionError,
      onFormClear,
      onFormCollapse,
      getCurrentText,
      currentStatusFilter,
      onAnimateOut,
      onReplySubmitted,
    ],
  )

  const retry = useCallback(async (): Promise<void> => {
    if (pendingReply) {
      await submit(pendingReply)
    }
  }, [pendingReply, submit])

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      clearOptimisticTimer()
    }
  }, [clearOptimisticTimer])

  return {
    isSubmitting,
    isOptimistic: isOptimisticReply,
    error: replyError || undefined,
    errorType: errorType || undefined,
    pendingReply: pendingReply || undefined,
    submit,
    retry,
  }
}
